import { action, makeObservable, observable } from 'mobx'

import { DefaultLandingPage } from '~/client/graph'
import BarState from '~/client/src/desktop/enums/SideBarState'
import DesktopRootStore from '~/client/src/desktop/stores/DesktopRoot.store'
import {
  getURLParam,
  isDeliveryInfoPageRequested,
  isProjectBasedPath,
  isProjectDeepLink,
  projectBased,
} from '~/client/src/shared/constants/commonRoutes'
import Photo from '~/client/src/shared/models/Photo'
import CommonStore from '~/client/src/shared/stores/ui/Common.store'

import desktopRoutes from '../../constants/desktopRoutes'
import DesktopInitialState from '../DesktopInitialState'

// localization: no display text to translate

export default class DesktopCommonStore extends CommonStore {
  @observable public isProjectSelectorDisplayed = true

  public constructor(
    protected state: DesktopInitialState,
    protected root: DesktopRootStore,
  ) {
    super(state, root)
    makeObservable(this)
  }

  public displayAuthSuccessRoute = () => {
    // Follow the user's original deep link when they asked for a concrete
    // in-app destination; otherwise fall through to the configured
    // default landing page (resolved by displayDefaultView — not always
    // home). Mirrors an authenticated direct visit, which renders the
    // requested path straight through the router (STX1-540). Replay the
    // full captured destination (path + query + hash) verbatim so no URL
    // component is dropped (STX1-582). Gate and replay both read initDeepLink
    // so they can't diverge (e.g. after the logout reset clears it).
    const path = (this.state.initDeepLink || '').split('?')[0].split('#')[0]
    if (isProjectDeepLink(path)) {
      this._displayView(this.state.initDeepLink)
      return
    }

    this.displayDefaultView()
  }

  public get viewToDisplay(): string {
    const {
      isDeliveriesDisabled,
      isLogisticsDisabled,
      isWorkflowsDisabled,
      isMaterialsDisabled,
      isMaterialResourcesDisabled,
      isAnalyticsDisabled,
      isEquipmentDisabled,
      isPhotosDisabled,
      isStruxTagsDisabled,
      isLocationsDisabled,
    } = this.state

    const defaultPage = this.defaultAvailablePage
    if (defaultPage) {
      return defaultPage
    }

    switch (true) {
      case !isLogisticsDisabled:
        return desktopRoutes.HOME
      case !isDeliveriesDisabled:
        return desktopRoutes.DELIVERIES
      case !isWorkflowsDisabled:
        return desktopRoutes.FORMS
      case !isMaterialsDisabled:
        return desktopRoutes.MATERIALS
      case !isMaterialResourcesDisabled:
        return desktopRoutes.MATERIAL_RESOURCES
      case !isAnalyticsDisabled:
        return desktopRoutes.ANALYTICS
      case !isEquipmentDisabled:
        return desktopRoutes.EQUIPMENT
      case !isPhotosDisabled:
        return desktopRoutes.FILES
      case !isStruxTagsDisabled:
        return desktopRoutes.STRUXTAGS
      case !isLocationsDisabled:
        return desktopRoutes.LOCATIONS
      default:
        return desktopRoutes.USER_PROFILE
    }
  }

  public displayActivitiesView() {
    this.displayDefaultView()
  }

  public displayNotifications() {
    this._displayView(desktopRoutes.NOTIFICATIONS)
  }

  public displayDeliveriesView() {
    this._displayView(desktopRoutes.DELIVERIES)
  }

  public openDeliveryView() {
    this.state.sideBarState = BarState.DeliveryCalendar
  }

  // TODO: [_displayDeliveryDetailsView] vs.[displayDeliveryDetailsView]
  public _displayDeliveryDetailsView = (deliveryId: string) => {
    this._displayView(
      desktopRoutes.DELIVERY_DETAILS.replace(
        getURLParam(desktopRoutes.DELIVERY_DETAILS),
        deliveryId,
      ),
    )
  }

  public displayDeliveryDetailsView() {
    this.state.sideBarState = BarState.DeliveryDetails
  }

  public displayPhotoView(photo: Photo) {
    this.state.displayedContentObjectId = photo.id
    this.state.sideBarState = BarState.Photo
  }

  public displaySharedDeliveryView() {
    return
  }

  public displaySameViewWithUpdatedProjectId() {
    if (!isProjectBasedPath()) {
      return
    }

    const path = '/' + location.pathname.split('/').slice(3).join('/')
    const route = projectBased(path)

    this._displayView(route + location.search)
  }

  @action.bound
  public openChat() {
    this.state.openChat()
  }

  public get defaultAvailablePage(): string | undefined {
    const {
      isDeliveriesDisabled,
      isLogisticsDisabled,
      isWorkflowsDisabled,
      isMaterialsDisabled,
      isMaterialResourcesDisabled,
      isAnalyticsDisabled,
      isEquipmentDisabled,
      isPhotosDisabled,
      isStruxTagsDisabled,
      isLocationsDisabled,
      activeProject: { defaultLandingPage = DefaultLandingPage.Home },
    } = this.state

    if (isDeliveryInfoPageRequested()) {
      return location.pathname
    }

    switch (true) {
      case defaultLandingPage === DefaultLandingPage.Home &&
        !isLogisticsDisabled:
        return desktopRoutes.HOME
      case defaultLandingPage === DefaultLandingPage.Deliveries &&
        !isDeliveriesDisabled:
        return desktopRoutes.DELIVERIES
      case defaultLandingPage === DefaultLandingPage.Forms &&
        !isWorkflowsDisabled:
        return desktopRoutes.FORMS
      case defaultLandingPage === DefaultLandingPage.Materials &&
        !isMaterialsDisabled:
        return desktopRoutes.MATERIALS
      case defaultLandingPage === DefaultLandingPage.MaterialResources &&
        !isMaterialResourcesDisabled:
        return desktopRoutes.MATERIAL_RESOURCES
      case defaultLandingPage === DefaultLandingPage.Analytics &&
        !isAnalyticsDisabled:
        return desktopRoutes.ANALYTICS
      case defaultLandingPage === DefaultLandingPage.Equipment &&
        !isEquipmentDisabled:
        return desktopRoutes.EQUIPMENT
      case defaultLandingPage === DefaultLandingPage.Photos &&
        !isPhotosDisabled:
        return desktopRoutes.FILES
      case defaultLandingPage === DefaultLandingPage.StruxTags &&
        !isStruxTagsDisabled:
        return desktopRoutes.STRUXTAGS
      case defaultLandingPage === DefaultLandingPage.Locations &&
        !isLocationsDisabled:
        return desktopRoutes.LOCATIONS
      case defaultLandingPage === DefaultLandingPage.Tracker:
        return undefined
      default:
        return undefined
    }
  }
}
